import { css } from '@emotion/css';
import { Box, Typography } from '@mui/material';
import { shadows } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { array, func, string } from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { Mention, MentionsInput as MentionsInputOrig } from 'react-mentions';

const useMentionsStyles = () => {
  const theme = useTheme();
  return useMemo(() => {
    const focused = css({
      background: theme.palette.primary.extraLight,
    });
    const suggestion = css({
      display: 'flex',
      alignItems: 'center',
      padding: '5px 10px',
      background: theme.white,
    });
    const display = css({ marginLeft: '10px' });

    return {
      root: css({
        maxHeight: 72,
        padding: '6px 0 7px',
        paddingLeft: '15px',
        position: 'relative',
        width: '100%',
      }),
      suggestionsPortal: css({
        position: 'absolute',
        minHeight: 100,
        minWidth: 200,
        backgroundColor: theme.white,
      }),
      mentions: css({
        position: 'relative',
        width: '100%',
        color: theme.typography.body1.color,
        cursor: 'text',
        fontSize: '0.8125rem',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        fontWeight: 400,
        lineHeight: '1.1876em',
      }),
      mentions__input: css({
        lineHeight: '1.3em',
        padding: 0,
        resize: 'none',
        font: 'inherit',
        color: 'currentColor',
        width: '100%',
        border: '1px solid transparent',
        height: '1.1876em',
        margin: 0,
        display: 'block',
        minWidth: 0,
        WebkitTapHighlightColor: 'transparent',
        '&:focus': {
          outline: 0,
        },
        '&::placeholder': {
          color: 'currentColor',
          opacity: 0.42,
          transition: 'opacity 200ms cubic-bezier(0.4,0,0.2,1) 0ms',
        },
      }),
      mentions__suggestions: css({
        padding: `${theme.spacing(1)} 0`,
        backgroundColor: `${theme.palette.white} !important`,
        borderRadius: '6px',
        boxShadow: shadows[3],
        maxHeight: 300,
        overflowY: 'auto',
      }),
      mentions__highlighter: css({
        zIndex: 1,
        pointerEvents: 'none',
        lineHeight: '1.3em',
      }),
      mention: css({
        color: theme.palette.mention,
        background: theme.palette.mentionBack,
        borderRadius: '3px',
      }),
      suggestionsPortalHost: css({
        zIndex: 10,
        position: 'absolute',
      }),
      suggestion,
      focused,
      display,
    };
  }, [theme]);
};

const MentionsInput = ({ data, onChange, name, onAdd, ...args }) => {
  const suggestionsPortalHost = useRef();
  const inputRef = useRef();
  const classes = useMentionsStyles();
  const handleOnChange = (event, markup, text, mentions) => {
    if (typeof onChange === 'function') {
      event.target.name = name;

      event.target.value = text;
      onChange(event, markup, text, mentions);
    }
  };
  const renderUserSuggestion = (suggestion, query, highlightedDisplay, index, focused) => {
    return (
      <div className={classNames(classes.suggestion, { [classes.focused]: focused })}>
        <Typography className={classes.display}>{suggestion.display}</Typography>
      </div>
    );
  };
  const handleAdd = (id, display, ...rest) => {
    const startPos = inputRef.current.selectionStart;
    if (typeof onAdd === 'function') {
      onAdd(id, display, ...rest);
    }
    setTimeout(() => {
      const el = document.getElementsByName(name)?.[0];
      if (el) {
        el.focus();
        el.selectionStart = startPos + display.length + 1;
        el.selectionEnd = startPos + display.length + 1;
      }
    }, 10);
  };
  return (
    <Box className={classes.root}>
      <Box ref={suggestionsPortalHost} className={classes.suggestionsPortalHost} />
      <MentionsInputOrig
        name={name}
        onChange={handleOnChange}
        {...args}
        inputRef={inputRef}
        className="mentions"
        classNames={classes}
        suggestionsPortalHost={suggestionsPortalHost.current}
      >
        <Mention
          appendSpaceOnAdd
          trigger="@"
          data={data}
          classNames={classes}
          className="mention"
          displayTransform={(id, display) => `@${display}`}
          renderSuggestion={renderUserSuggestion}
          onAdd={handleAdd}
        />
      </MentionsInputOrig>
    </Box>
  );
};

MentionsInput.propTypes = {
  name: string.isRequired,
  data: array.isRequired,
  onChange: func,
  onAdd: func,
};

export default MentionsInput;
